

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SearchBar from './SearchBar';

const HeroSlider = ({
  slides = [],
  categories = [],
  searchQuery,
  onSearchChange,
  onSearch,
  onQuickSearch
}) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [transactionType, setTransactionType] = useState('rent');
  const [selectedPropertyType, setSelectedPropertyType] = useState('all');
  const [bedrooms, setBedrooms] = useState('any');
  const [maxBudget, setMaxBudget] = useState('');
  const slideRef = useRef(null);

  const propertyTypeOptions = categories.length > 0 ? categories : [
    { id: 'all', category: 'All Property Types' },
    { id: 'apartment', category: 'Apartment / Flat' },
    { id: 'house', category: 'Standalone House' },
    { id: 'office', category: 'Office Space' },
    { id: 'land', category: 'Land / Plot' }
  ];

  const handleSearchSubmit = () => {
    onSearch({
      location: searchQuery,
      transactionType,
      categoryId: selectedPropertyType,
      bedrooms,
      maxBudget
    });
  };

  const handleBudgetChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setMaxBudget(numericValue);
  };

  // Define unique content themes for alternating slides if not provided by backend data
  const slideThemes = [
    {
      badge: "Verified & Trusted Local Agents",
      badgeIcon: "ShieldCheck",
      titlePrefix: "Hire Trusted Brokers to",
      titleHighlight: "Hunt Your Next Rental",
      subtitle: "Stop wasting time wandering neighborhoods. Connect with certified local brokers who will find, vet, and secure your perfect property in any area.",
      ctaText: "Find a Broker",
      ctaIcon: "UserCheck",
      stats: [
        { value: "100%", label: "Vetted Brokers" },
        { value: "Any Area", label: "Targeted Search" },
        { value: "Zero Hassle", label: "Rental Hunting" }
      ]
    },
    {
      badge: "Direct From Verified Owners",
      badgeIcon: "Home",
      titlePrefix: "Explore Thousands of",
      titleHighlight: "Verified Property Listings",
      subtitle: "Browse hand-picked, fraud-free apartments, commercial spaces, and land. What you see is exactly what you get—guaranteed.",
      ctaText: "Browse Listings",
      ctaIcon: "Search",
      stats: [
        { value: "5,000+", label: "Verified Homes" },
        { value: "Real-Time", label: "Availability" },
        { value: "0% Fraud", label: "Guarantee" }
      ]
    },
    {
      badge: "Tailored Smart Property Match",
      badgeIcon: "Sparkles",
      titlePrefix: "Tell Us Your Budget,",
      titleHighlight: "We'll Do The Rest",
      subtitle: "Specify your neighborhood, target rent, and desired amenities. Let our smart platform match you with specialized on-ground experts instantly.",
      ctaText: "Start Smart Match",
      ctaIcon: "Sliders",
      stats: [
        { value: "24 Hours", label: "Average Match" },
        { value: "Custom", label: "Budgets Welcomed" },
        { value: "24/7", label: "Expert Support" }
      ]
    }
  ];

  useEffect(() => {
    if (slides.length <= 1 || !isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [slides.length, isAutoPlaying]);

  const handleTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) nextSlide();
    else if (isRightSwipe) prevSlide();
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (slides.length <= 1) return;

      if (event.key === 'ArrowRight') nextSlide();
      else if (event.key === 'ArrowLeft') prevSlide();
      else if (event.key === ' ') {
        event.preventDefault();
        setIsAutoPlaying((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slides.length]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  if (slides.length === 0) {
    return (
      <div className="relative w-full h-[600px] md:h-[700px] bg-gradient-to-br from-primary/20 via-primary/10 to-secondary/20 flex items-center justify-center overflow-hidden">
        <div className="text-center z-10">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-spin">
            <Icon name="Users" size={32} className="text-primary" />
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-2">Welcome to Hire Broker</h2>
          <p className="text-muted-foreground text-lg">Connecting you to trusted brokers and verified properties seamlessly.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={slideRef}
      className="relative w-full h-[600px] md:h-[700px] overflow-hidden group"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides */}
      {slides.map((slide, index) => {
        // Rotate through themes based on index if individual slide content isn't passed down explicitly
        const currentTheme = slideThemes[index % slideThemes.length];

        return (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1500 ease-in-out ${
              index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            {/* Background Image with Parallax Effect */}
            <div className="relative w-full h-full overflow-hidden">
              <Image
                src={slide.image_url || slide.image}
                alt={slide.title || `Slide ${index + 1}`}
                className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${
                  index === currentSlide ? 'scale-100' : 'scale-110'
                }`}
              />

              {/* Dynamic Gradient Overlays */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>
            </div>

            {/* Hero Content */}
            <div className={`absolute inset-0 flex items-center transition-all duration-1000 delay-300 ${
              index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
                <div className="max-w-2xl">
                  {/* Dynamic Badge */}
                 
                  <div className={`inline-flex items-center px-4 py-2 bg-white rounded-xl shadow-sm border border-primary/30 text-primary text-sm font-medium mb-6 transition-all duration-700 delay-500 ${
                    index === currentSlide ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}>
                    <Icon name={slide.badgeIcon || currentTheme.badgeIcon} size={16} className="mr-2" />
                    {slide.badge || currentTheme.badge}
                  </div>

                  {/* Dynamic Headline */}
                  <h1 className={`text-4xl md:text-5xl lg:text-5xl font-bold text-white mb-5 leading-tight transition-all duration-700 delay-700 ${
                    index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}>
                    {slide.titlePrefix || currentTheme.titlePrefix}
                    <span className="block text-primary bg-secondary bg-clip-text text-transparent mt-2">
                      {slide.titleHighlight || currentTheme.titleHighlight}
                    </span>
                  </h1>

                  {/* Dynamic Subtitle */}
                  <p className={`text-lg md:text-xl text-white/90 mb-8 max-w-xl leading-relaxed transition-all duration-700 delay-1000 ${
                    index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}>
                    {slide.subtitle || currentTheme.subtitle}
                  </p>

                  {/* Dynamic CTA Buttons */}
                  <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-1200 ${
                    index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}>
                    <Button
                      size="lg"
                      className="bg-primary hover:bg-primary/90 text-white px-8 py-4 text-lg font-semibold shadow-2xl hover:shadow-primary/25 transition-all duration-300 hover:scale-105"
                      onClick={handleSearchSubmit}
                    >
                      <Icon name={slide.ctaIcon || currentTheme.ctaIcon} size={20} className="mr-2" />
                      {slide.ctaText || currentTheme.ctaText}
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-4 text-lg font-semibold transition-all duration-300 hover:scale-105"
                      onClick={() => navigate('/about')}
                    >
                      <Icon name="Info" size={20} className="mr-2" />
                      How It Works
                    </Button>
                  </div>

                  {/* Dynamic Stats */}
                  <div className={`flex flex-wrap gap-8 mt-12 transition-all duration-700 delay-1500 ${
                    index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}>
                    {(slide.stats || currentTheme.stats).map((stat, sIdx) => (
                      <div key={sIdx} className="text-white">
                        <div className="text-2xl font-bold">{stat.value}</div>
                        <div className="text-white/70 text-sm">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical Search Sidebar */}
          
            
            {/* Dynamic Desktop Right Panel - Sidebar Search vs. Mobile App Showcase */}
      <div className="absolute right-16 top-1/2 -translate-y-1/2 z-20 hidden lg:block">
        <div className="w-[420px] max-w-[420px] transition-all duration-500">
          {index % slideThemes.length === 0 ? (
            /* APP SHOWCASE MODE: Displayed during the "Hire Trusted Brokers" theme */
            <div className="relative backdrop-blur-2xl border border-white/10 rounded-3xl p-4 shadow-2xl flex flex-col items-center text-center animate-fadeIn bg-gradient-to-b from-white/5 to-white/3">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-primary/20 via-transparent to-secondary/10 blur-xl opacity-40 pointer-events-none" />

              {/* Mobile App Mockup Container (Static image) */}
              <div className="relative w-full rounded-[2rem] overflow-hidden bg-black/5 border border-white/5 shadow-inner">
                <div className="absolute -top-6 right-4 bg-white/10 px-3 py-1 rounded-full text-[11px] text-white/90 backdrop-blur">
                  <Icon name="Sparkles" size={14} className="inline-block mr-1 text-yellow-300" />
                  Featured
                </div>
                <div className="p-2 flex items-center justify-center">
                  <Image
                    src="/assets/images/slider.png"
                    alt="The HireBroker App"
                    className="w-full h-96 object-cover rounded-2xl mx-auto shadow-xl"
                  />
                </div>
              </div>

              {/* App Info */}
              <div className="w-full mt-4">
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm font-semibold text-white">HireBroker App</div>
                  <div className="text-xs text-white/70 px-2 py-1 bg-white/5 rounded-full">v2.1</div>
                </div>
                <p className="text-[11px] text-white/60 mt-2 truncate">Hire trusted brokers, chat instantly, and secure visits — all from your phone.</p>

                {/* Quick App Store Actions */}
                <div className="mt-3 w-full grid grid-cols-2 gap-2">
                  <a
                    href="https://apps.apple.com/ug/app/hire-broker/id6476240355"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-black/90 text-white py-2 px-3 rounded-lg hover:scale-105 transform transition-all"
                  >
                    <Icon name="Apple" size={14} />
                    <div className="text-left">
                      <span className="text-[9px] opacity-60 block leading-none">Download on the</span>
                      <span className="text-[11px] font-bold leading-none">App Store</span>
                    </div>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.hirebroker.app&hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-white text-black py-2 px-3 rounded-lg hover:scale-105 transform transition-all"
                  >
                    <Icon name="Play" size={12} fill="black" />
                    <div className="text-left">
                      <span className="text-[9px] opacity-60 block leading-none">Get it on</span>
                      <span className="text-[11px] font-bold leading-none">Google Play</span>
                    </div>
                  </a>
                </div>

                <div className="mt-3 flex items-center justify-center gap-3 text-[11px] text-white/70">
                  <div className="flex items-center gap-1">
                    <Icon name="Star" size={12} className="text-yellow-300" />
                    <span className="font-medium">4.8</span>
                    <span className="opacity-70">(12k+)</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
      /* SEARCH FILTER MODE: Displayed for property listing slides */
      <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl animate-fadeIn">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-1">Find Your Next Home</h3>
          <p className="text-gray-500 text-xs">Instantly scan thousands of verified, fraud-free listings.</p>
        </div>

        <div className="space-y-4">
            <div className="grid grid-cols-2 p-1 bg-gray-100 rounded-xl">
              <button
                type="button"
                className={`py-2 text-xs font-semibold rounded-lg transition-colors duration-200 ${transactionType === 'rent' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`}
                onClick={() => setTransactionType('rent')}
              >
                For Rent
              </button>
              <button
                type="button"
                className={`py-2 text-xs font-semibold rounded-lg transition-colors duration-200 ${transactionType === 'sell' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`}
                onClick={() => setTransactionType('sell')}
              >
                For Sale
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                <Icon name="MapPin" size={14} className="text-gray-400" /> Location / Area
              </label>
              <SearchBar searchQuery={searchQuery} onSearchChange={onSearchChange} onSearch={handleSearchSubmit} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                  <Icon name="Home" size={14} className="text-gray-400" /> Property Type
                </label>
                <select
                  className="w-full text-xs bg-gray-50 border border-gray-200 rounded-xl p-3 text-gray-800 font-medium focus:outline-none appearance-none cursor-pointer"
                  value={selectedPropertyType}
                  onChange={(e) => setSelectedPropertyType(e.target.value)}
                >
                  <option value="all">All Property Types</option>
                  {propertyTypeOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                  <Icon name="BedDouble" size={14} className="text-gray-400" /> Bedrooms
                </label>
                <select
                  className="w-full text-xs bg-gray-50 border border-gray-200 rounded-xl p-3 text-gray-800 font-medium focus:outline-none appearance-none cursor-pointer"
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                >
                  <option value="any">Any Rooms</option>
                  <option value="1">1 Bedroom</option>
                  <option value="2">2 Bedrooms</option>
                  <option value="3">3+ Bedrooms</option>
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                <Icon name="Coins" size={14} className="text-gray-400" /> Max Budget
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={maxBudget}
                  onChange={handleBudgetChange}
                  placeholder="e.g., 1500000"
                  className="w-full text-xs bg-gray-50 border border-gray-200 rounded-xl p-3 pr-12 text-gray-800 font-medium focus:outline-none"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 uppercase">/ Month</span>
              </div>
            </div>

            <Button
              size="lg"
              className="w-full bg-primary hover:bg-primary/90 text-white py-3.5 text-sm font-bold rounded-xl shadow-lg transition-all"
              onClick={handleSearchSubmit}
            >
              <Icon name="Search" size={16} className="mr-2" /> Search Matching Properties
            </Button>
          </div>
        <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-gray-400 text-[10px] font-medium">
          <div className="flex items-center gap-1">
            <Icon name="ShieldCheck" size={12} className="text-green-500" /> <span>100% Vetted Listings</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="UserCheck" size={12} className="text-blue-500" /> <span>Direct Owner Connections</span>
          </div>
        </div>
      </div>
    )}
  </div>
</div>






            
          </div>
        );
      })}

     

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-md border border-white/20 hover:border-white/40 hover:scale-110 shadow-lg group"
            aria-label="Previous slide"
          >
            <Icon name="ChevronLeft" size={24} className="group-hover:-translate-x-1 transition-transform duration-200" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-md border border-white/20 hover:border-white/40 hover:scale-110 shadow-lg group"
            aria-label="Next slide"
          >
            <Icon name="ChevronRight" size={24} className="group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </>
      )}

      {/* Progress Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-40 left-1/2 -translate-x-1/2 flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative transition-all duration-300 ${
                index === currentSlide ? 'w-8 h-3 bg-primary' : 'w-3 h-3 bg-white/50 hover:bg-white/70'
              } rounded-full overflow-hidden`}
              aria-label={`Go to slide ${index + 1}`}
            >
              {index === currentSlide && (
                <div className="absolute inset-0 bg-primary animate-pulse"></div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Auto-play Switch */}
      {slides.length > 1 && (
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-md border border-white/20 hover:border-white/40 hover:scale-110 shadow-lg"
          aria-label={isAutoPlaying ? 'Pause auto-play' : 'Start auto-play'}
        >
          <Icon name={isAutoPlaying ? 'Pause' : 'Play'} size={16} />
        </button>
      )}

      {/* Index Counter */}
      {slides.length > 1 && (
        <div className="absolute top-6 left-6 bg-black/30 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium border border-white/10">
          {currentSlide + 1} / {slides.length}
        </div>
      )}
    </div>
  );
};

export default HeroSlider;
